import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Info, RefreshCw } from "lucide-react";
import { formatMoney } from "../api";
import { useApi } from "../hooks";
import { EmptyState, ErrorState, MetricCard, PageHeader, PageSkeleton, StatusBadge, monogram } from "../components/Ui";
import FilterPill from "../components/FilterPill";
import { AreaChart, BarList, ChartCard, ChartEmpty } from "../components/Charts";
import { AdsRange } from "../components/AdsRange";
import {
  changeTone, comparisonNotes, sortPlatforms, filterClients, fmtInt, fmtMoneyWhole, fmtNum1, fmtRoas, platformName, shortDate, sortClients, trendPoints,
  type AdsOverview, type MetricKey, type SortKey, type TrendMetric,
} from "../ads";
import { reportRangeQuery } from "../date-range";

/** The day the sync started filing LSA under its own platform (utils/adsReport.js LSA_SPLIT_FROM). */
const LSA_SPLIT = "2026-09-21";
/** Projected month spend above budget x this is "over pace" (utils/adsReport.js OVER_PACE_RATIO). */
const OVER_PACE = 1.1;
const overPace = (b: { budget: number; projected: number | null } | null | undefined) => Boolean(b && b.budget > 0 && b.projected != null && b.projected > b.budget * OVER_PACE);
/** has_account: the overview lists a client with a monthly budget and no ad account linked (routes/ads.js, when present). */
type Row = AdsOverview["clients"][number] & { has_account?: boolean };

const money = (v: number | null) => formatMoney(v, true);
const moneyExact = (v: number | null) => formatMoney(v);
// Plain stat tiles: the same icon on Clicks and Cost per click (and on Conversions
// and Cost per conversion) said nothing, so the tiles carry just label and number.
export const TILES: { key: MetricKey; label: string; format: (v: number | null) => string }[] = [
  { key: "spend", label: "Spend", format: money },
  { key: "clicks", label: "Clicks", format: fmtInt },
  { key: "cpc", label: "Cost per click", format: moneyExact },
  { key: "conversions", label: "Conversions", format: fmtNum1 },
  { key: "cpa", label: "Cost per conversion", format: moneyExact },
  { key: "calls", label: "Calls from ads", format: fmtInt },
  { key: "roas", label: "ROAS", format: fmtRoas },
];
const TREND: { key: TrendMetric; label: string }[] = [
  { key: "spend", label: "Spend" }, { key: "clicks", label: "Clicks" }, { key: "conversions", label: "Conversions" },
];
const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "name", label: "Client" }, { key: "spend", label: "Spend" }, { key: "clicks", label: "Clicks" }, { key: "cpc", label: "CPC" },
  { key: "conversions", label: "Conv." }, { key: "cpa", label: "CPA" }, { key: "calls", label: "Calls" }, { key: "budget", label: "Budget" },
];

export function Movement({ metric, value }: { metric: MetricKey; value: number | null | undefined }) {
  if (value === null || value === undefined) return null;
  return <small className={`trend ${changeTone(metric, value)}`}>{value > 0 ? "+" : ""}{value.toFixed(1)}%</small>;
}

export function TrendControls({ metric, setMetric, platform, setPlatform, platforms }: {
  metric: TrendMetric; setMetric: (m: TrendMetric) => void; platform: string; setPlatform: (p: string) => void; platforms: string[];
}) {
  return (
    <span className="ads-trend-controls">
      <span className="segmented-control compact" aria-label="Trend metric">
        {TREND.map((m) => <button key={m.key} type="button" className={metric === m.key ? "active" : ""} aria-pressed={metric === m.key} onClick={() => setMetric(m.key)}>{m.label}</button>)}
      </span>
      {platforms.length > 1 && (
        <span className="segmented-control compact" aria-label="Trend platform">
          {["all", ...platforms].map((p) => <button key={p} type="button" className={platform === p ? "active" : ""} aria-pressed={platform === p} onClick={() => setPlatform(p)}>{p === "all" ? "All" : platformName(p)}</button>)}
        </span>
      )}
    </span>
  );
}

export default function Ads() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const range = reportRangeQuery(params);
  // A new range keeps the current numbers up (dimmed) until the new ones arrive.
  const { data, loading, refreshing, error, reload } = useApi<AdsOverview>(`/api/ads/overview?${range}`, { keepPrevious: true });
  const [metric, setMetric] = useState<TrendMetric>("spend");
  const [platform, setPlatform] = useState("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "spend", dir: "desc" });
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [notesOpen, setNotesOpen] = useState(false);
  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorState message={error || "No ads data was returned."} onRetry={reload} />;

  const platformKeys = sortPlatforms(data.platforms.map((p) => p.platform));
  const trendPlatform = platformKeys.includes(platform) ? platform : "all";
  const onlyPlatform = only === "all" || platformKeys.includes(only) ? only : "all";
  const rows = sortClients(filterClients(data.clients, { platform: onlyPlatform, q }), sort.key, sort.dir);
  const hasData = data.daily.some((d) => d.spend || d.clicks || d.conversions);
  const compare = data.comparison_clients
    ? `Compared with ${shortDate(data.period.previous.from)} to ${shortDate(data.period.previous.to)}.`
    : "No client has data for the whole earlier period, so there is no comparison.";
  // The rest of the fine print sits behind the info button, one line stays under the tiles.
  const compareMore = data.comparison_clients
    ? [`Changes cover the ${data.comparison_clients} of ${data.clients.length} clients with data for that whole period.`, comparisonNotes(data.lsa_split_from, data.calls_from, shortDate)].filter(Boolean).join(" ")
    : "";
  // Calls are recorded from the first sync: a range that starts earlier only has calls since then.
  const callsSince = data.calls_from && data.period.from < data.calls_from ? `Since ${shortDate(data.calls_from)}` : undefined;
  // The budget is this month so far: shown only when the range reaches this month. Its
  // column names the month ("Nov budget used"), since Spend beside it covers the range.
  const monthStart = `${data.today.slice(0, 7)}-01`;
  const showBudget = data.period.to >= new Date(Date.parse(monthStart) - 86400000).toISOString().slice(0, 10);
  const budgetLabel = `${new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(new Date(`${monthStart}T12:00:00Z`))} budget used`;
  const columns = COLUMNS.filter((c) => c.key !== "budget" || showBudget).map((c) => (c.key === "budget" ? { ...c, label: budgetLabel } : c));
  const open = (id: number) => navigate(`/ads/${id}?${range}`);
  const toggleSort = (key: SortKey) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <>
      <PageHeader
        title="Ads"
        description={`${data.clients.length} client accounts, ${shortDate(data.period.from)} to ${shortDate(data.period.to)}`}
        actions={<><AdsRange /><button className="button secondary" disabled={refreshing} onClick={reload}><RefreshCw className={refreshing ? "spin" : ""} aria-hidden="true" />{refreshing ? "Refreshing…" : "Refresh"}</button></>}
      />
      <div className={refreshing ? "overview-body is-switching" : "overview-body"} aria-busy={refreshing}>
      {data.unavailable.length > 0 && <p className="ads-unavailable" role="status">Some figures could not be loaded right now. Refresh to try again.</p>}
      <section className="metric-grid ads-metrics" aria-label="Ads totals">
        {TILES.map((tile) => (
          <MetricCard key={tile.key}
            label={tile.key === "roas" && data.totals.revenue_clients ? `ROAS (${data.totals.revenue_clients} ${data.totals.revenue_clients === 1 ? "client" : "clients"})` : tile.label}
            value={tile.format(data.totals[tile.key])}
            change={data.change[tile.key] ?? null} tone={changeTone(tile.key, data.change[tile.key])}
            detail={tile.key === "roas" ? (data.totals.revenue_clients ? undefined : "No revenue tracked") : tile.key === "calls" ? callsSince : undefined} />
        ))}
      </section>
      <div className="ads-note ads-compare">
        <span>{compare}</span>
        {compareMore && <button type="button" className="ads-note-toggle" aria-expanded={notesOpen} aria-controls="ads-compare-more" aria-label="How these numbers compare" title="How these numbers compare" onClick={() => setNotesOpen((v) => !v)}><Info aria-hidden="true" /></button>}
        {compareMore && notesOpen && <p id="ads-compare-more" className="ads-note-more">{compareMore}</p>}
      </div>

      <div className="dashboard-charts ads-charts">
        <ChartCard className="ads-trend" eyebrow="Ads" title="Daily trend" meta={<TrendControls metric={metric} setMetric={setMetric} platform={trendPlatform} setPlatform={setPlatform} platforms={platformKeys} />}>
          {hasData ? (
            <AreaChart integer={metric !== "spend"} data={trendPoints(data.daily, metric, trendPlatform).map((p) => ({ label: shortDate(p.date), value: p.value }))}
              format={metric === "spend" ? (v) => formatMoney(v, true) : fmtInt}
              caption={`${TREND.find((m) => m.key === metric)!.label} per day, ${trendPlatform === "all" ? "all platforms" : platformName(trendPlatform)}`} />
          ) : <ChartEmpty>No ads data in this range.</ChartEmpty>}
          {(trendPlatform === "google_ads" || trendPlatform === "lsa") && data.period.from < LSA_SPLIT && <p className="ads-note chart-note">Before {shortDate(LSA_SPLIT)}, LSA for accounts that also run Search is counted in Google Ads.</p>}
        </ChartCard>
        <ChartCard eyebrow="Ads" title="Spend by client" meta={`${data.period.days} days`}>
          {data.clients.some((c) => c.metrics.spend > 0)
            ? <BarList limit={8} data={[...data.clients].sort((a, b) => b.metrics.spend - a.metrics.spend).map((c) => ({ label: c.name, value: c.metrics.spend }))} format={(v) => formatMoney(v, true)} caption="Spend by client" />
            : <ChartEmpty>No spend in this range.</ChartEmpty>}
        </ChartCard>
        <ChartCard eyebrow="Ads" title="Conversions by client" meta={`${data.period.days} days`}>
          {data.clients.some((c) => c.metrics.conversions > 0)
            ? <BarList limit={8} data={[...data.clients].sort((a, b) => b.metrics.conversions - a.metrics.conversions).map((c) => ({ label: c.name, value: c.metrics.conversions }))} format={fmtNum1} caption="Conversions by client" />
            : <ChartEmpty>No conversions in this range.</ChartEmpty>}
        </ChartCard>
        <ChartCard eyebrow="Ads" title="Spend by platform" meta={`${data.period.days} days`}>
          {data.platforms.some((p) => p.spend > 0)
            ? <BarList data={data.platforms.map((p) => ({ label: `${platformName(p.platform)} (${p.clients} ${p.clients === 1 ? "client" : "clients"})`, value: p.spend }))} format={(v) => formatMoney(v, true)} caption="Spend by ad platform" />
            : <ChartEmpty>No platform spend in this range.</ChartEmpty>}
        </ChartCard>
      </div>

      <section className="panel">
        <div className="panel-heading">
          <div><h2>Client accounts</h2></div>
          <div className="ads-controls">
            <input type="search" placeholder="Find a client" aria-label="Find a client" value={q} onChange={(e) => setQ(e.target.value)} />
            {/* Phones show rows without column headers, so sorting moves into a pill there.
                Spend, the default, comes first, so the pill is only tinted once someone changes it. */}
            <span className="ads-sort-pill">
              <FilterPill label="Sort" value={sort.key} onChange={(value) => { const key = value as SortKey; setSort({ key, dir: key === "name" ? "asc" : "desc" }); }}
                options={[...columns.filter((c) => c.key === "spend"), ...columns.filter((c) => c.key !== "spend")].map((c) => ({ value: c.key, name: c.label }))} />
            </span>
            <FilterPill label="Platform" value={onlyPlatform} onChange={setOnly}
              options={[{ value: "all", name: "All" }, ...platformKeys.map((p) => ({ value: p, name: platformName(p) }))]} />
          </div>
        </div>
        {rows.length ? (
          <div className="table-wrap">
            <table className="ads-table ads-clients ads-portfolio clickable">
              <thead><tr>
                {columns.map((c) => (
                  <th key={c.key} className={c.key === "name" ? undefined : "num"} aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                    <button type="button" className="sort-button" onClick={() => toggleSort(c.key)}>{c.label}{sort.key === c.key ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}</button>
                  </th>
                ))}
                <th>Flags</th>
              </tr></thead>
              <tbody>
                {(rows as Row[]).map((c) => {
                  const pace = overPace(c.budget);
                  const noAccount = c.has_account === false;
                  const flagged = c.open_anomalies > 0 || c.stale || pace || noAccount;
                  return (
                  <tr key={c.id} onClick={(e) => { if (!(e.target as HTMLElement).closest("a")) open(c.id); }}>
                    <td><Link className="client-cell" to={`/ads/${c.id}?${range}`}><span className="client-monogram">{monogram(c.name)}</span><span><strong>{c.name}</strong><small>{c.platforms.map(platformName).join(", ") || "No data in range"}</small></span></Link></td>
                    <td className="number-cell num" data-label="Spend">{fmtMoneyWhole(c.metrics.spend)}<Movement metric="spend" value={c.change.spend} /></td>
                    <td className="number-cell num" data-label="Clicks">{fmtInt(c.metrics.clicks)}<Movement metric="clicks" value={c.change.clicks} /></td>
                    <td className="number-cell num" data-label="CPC">{formatMoney(c.metrics.cpc)}</td>
                    <td className="number-cell num" data-label="Conv.">{fmtNum1(c.metrics.conversions)}<Movement metric="conversions" value={c.change.conversions} /></td>
                    <td className="number-cell num" data-label="CPA">{formatMoney(c.metrics.cpa)}<Movement metric="cpa" value={c.change.cpa} /></td>
                    <td className="number-cell num" data-label="Calls">{fmtInt(c.metrics.calls)}</td>
                    {showBudget && <td className={`number-cell num${pace ? " is-over-pace" : ""}`} data-label={budgetLabel} data-empty={c.budget ? undefined : "true"}>{c.budget ? `${formatMoney(c.budget.month_spend, true)} of ${formatMoney(c.budget.budget, true)}` : "—"}{c.budget && <small>{`${c.budget.used_pct ?? 0}% used`}{c.budget.projected ? `, on pace for ${formatMoney(c.budget.projected, true)}` : ""}</small>}</td>}
                    <td data-label="Flags" data-empty={flagged ? undefined : "true"}><span className="ads-flags">
                      {c.open_anomalies > 0 && <StatusBadge tone="danger">{`${c.open_anomalies} ${c.open_anomalies === 1 ? "anomaly" : "anomalies"}`}</StatusBadge>}
                      {c.stale && <StatusBadge tone="warning">Stale</StatusBadge>}
                      {pace && <StatusBadge tone="warning">Over pace</StatusBadge>}
                      {noAccount && <StatusBadge tone="warning">No ad account linked</StatusBadge>}
                    </span></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={<AlertTriangle aria-hidden="true" />} title="No client accounts match" description="Clear the search or choose All platforms." />}
      </section>
      </div>
    </>
  );
}
